import React from 'react';
import { View, StyleSheet, Platform } from 'react-native';

import { layout } from '../theme';
import { StepHeader, type StepHeaderVariant } from './StepHeader';

const isWeb = Platform.OS === 'web';

/** Web: UOM row above title (tail steps). Native: title-first compact chrome. */
export function stepHeaderVariantForPlatform(): StepHeaderVariant {
  return Platform.OS === 'web' ? 'uom-then-title' : 'title-with-uom';
}

export type StepShellProps = {
  /** H1 for the current step (Create Request / Request Detail) */
  title: string;
  /** Renders before the step H1 (e.g. drill-in back control on web). */
  titleLeading?: React.ReactNode;
  /**
   * Typically `<PermissionAwareField>…<UOMSelector useGlobalState /></PermissionAwareField>`.
   * Omit for chrome without UOM (e.g. Devices / `title-only`).
   */
  uomControl?: React.ReactNode;
  /** Same row as UOM when using default `uom-then-title` header (e.g. view toggle + PDF on Request Detail). */
  uomRowEnd?: React.ReactNode;
  /**
   * UOM + title layout. Default `uom-then-title` when UOM is present; `title-only` when omitted.
   * Opportunity / Site information use {@link stepHeaderVariantForPlatform} so web matches tail steps
   * while native keeps title-first compact chrome.
   */
  headerVariant?: StepHeaderVariant;
  /** Renders between the header and the subsection/card content (e.g. offline callout) */
  afterTitle?: React.ReactNode;
  testID?: string;
  children: React.ReactNode;
};

/**
 * Column wrapper for one step: shared max-width (web), `StepHeader`, then step body
 * (subsections / `StepForm` blocks).
 */
export const StepShell: React.FC<StepShellProps> = ({
  title,
  titleLeading,
  uomControl,
  uomRowEnd,
  headerVariant = uomControl != null || uomRowEnd != null
    ? 'uom-then-title'
    : 'title-only',
  afterTitle,
  testID,
  children,
}) => (
  <View style={[styles.root, isWeb && styles.rootWeb]}>
    <StepHeader
      title={title}
      titleLeading={titleLeading}
      variant={headerVariant}
      uomControl={uomControl}
      uomRowEnd={uomRowEnd}
      afterTitle={afterTitle}
      testID={testID}
    />
    {children}
  </View>
);

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: '100%',
  },
  rootWeb: {
    maxWidth: layout.pdPhase1.contentMaxWidth,
    alignSelf: 'center',
  },
});

StepShell.displayName = 'StepShell';
